// PPS CRM - Reminders Module (Client reminders)
import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface ClientReminder {
  id: string;
  weekOf: string;
  paymentDate: string;
  daysUntilPayment: number;
  contactStatus: 'PENDING' | 'CONTACTED' | 'RESOLVED';
  client: { id: string; fullLegalName: string; packageType: string };
}

export default function Reminders() {
  const [reminders, setReminders] = useState<ClientReminder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await api.get('/api/reminders');
      setReminders(response.data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const markContacted = async (id: string) => {
    try {
      await api.patch(`/api/reminders/${id}/contact`);
      fetchReminders();
    } catch (error) {
      console.error('Error updating reminder:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'CONTACTED':
        return 'bg-green-100 text-green-800';
      case 'RESOLVED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const overdue = reminders.filter((r) => new Date(r.paymentDate) < new Date() && r.contactStatus === 'PENDING');

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Client Reminders</h2>
      </div>

      {overdue.length > 0 && (
        <div className="card mb-6 border-l-4 border-red-500">
          <h3 className="font-semibold text-red-600 mb-3">Overdue This Week</h3>
          <div className="space-y-2">
            {overdue.map((reminder) => (
              <div key={reminder.id} className="flex justify-between items-center p-3 bg-red-50 rounded">
                <div>
                  <span className="font-medium">{reminder.client.fullLegalName}</span>
                  <span className="text-dark-gray ml-2">- {reminder.client.packageType}</span>
                </div>
                <button
                  onClick={() => markContacted(reminder.id)}
                  className="text-green-600 hover:underline text-sm"
                >
                  Mark Contacted
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="font-semibold mb-4">All Reminders</h3>

        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : reminders.length === 0 ? (
          <div className="p-8 text-center text-dark-gray">No reminders found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-light-gray">
              <tr>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-left">Payment Date</th>
                <th className="px-4 py-3 text-left">Days Until</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reminders.map((reminder) => (
                <tr key={reminder.id} className="border-b border-light-gray hover:bg-light-gray">
                  <td className="px-4 py-3">
                    <div className="font-medium">{reminder.client.fullLegalName}</div>
                    <div className="text-sm text-dark-gray">{reminder.client.packageType}</div>
                  </td>
                  <td className="px-4 py-3">
                    {new Date(reminder.paymentDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">{reminder.daysUntilPayment}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(reminder.contactStatus)}`}>
                      {reminder.contactStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {reminder.contactStatus === 'PENDING' && (
                      <button
                        onClick={() => markContacted(reminder.id)}
                        className="text-green-600 hover:underline text-sm"
                      >
                        Mark Contacted
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
